import type { DefaultFieldValues } from '@kizen/api';
import type { EntityRecordDetail } from '@kizen/api/records';
import {
  isEmailValue,
  isFirstNameValue,
  isLastNameValue,
  isMobilePhoneValue,
} from '@kizen/checks/field-value';
import styled from '@emotion/styled';
import { useMemo } from 'react';
import { colorsButton } from 'app/colors';
import Icon from '__components/Kizen/Icon';
import IconButton from '__components/Kizen/IconButton';
import {
  ListCardCell,
  ListCardItem,
  ListCardRow,
  ListCardSortButton,
} from 'components/Layout/ListCard';
import StylePassthrough from 'components/Kizen/StylePassthrough';
import { TextEllipsis } from '__components/Kizen/Table';
import { layers } from 'app/spacing';
import { useTranslation } from 'react-i18next';
import { StyledListCardHeader, StyledListCardItem } from './styles';

type ContactListItemProps = {
  record: EntityRecordDetail;
};

type ContactListHeaderProps = {
  label: string;
  sort: { column: string } | null;
  onChangeSort?(value: {
    column: 'full_name';
    direction: 'asc' | 'desc';
  }): void;
};

export const CheckboxStyles = styled(StylePassthrough)`
  label,
  label& {
    margin: 0;
    padding: 0;
    // Change hover color to dark grey
    &:hover input[type='checkbox']:not(:checked) + .CheckboxInputFacade {
      border-color: var(--color-border-darker);
    }
  }
  .CheckboxText {
    display: none;
  }
  .CheckboxInputFacade {
    // In chrome the input facade's position: relative creates a new stacking
    // context, causing this element to appear on top of the page's sticky header.
    z-index: ${layers.content(0)};
    margin-left: 0;
  }
`;

const ContactItemCell = styled(ListCardCell)`
  > :first-child {
    flex: 1;
  }
  > :last-child:not(:first-child) {
    margin-left: 5px;
  }
`;

const EmailIcon = styled(Icon)`
  && svg {
    width: 16px;
    height: auto;
  }
`;

const PhoneIcon = styled(Icon)`
  && svg {
    width: 12px;
    height: auto;
  }
`;

const NO_VALUE = '—';

export const EmptyContactListItem = ListCardItem;

export const ContactListItem = ({ record }: ContactListItemProps) => {
  const { t } = useTranslation();

  const { email, full_name, mobile_phone } = useMemo(() => {
    const result: Pick<
      Partial<DefaultFieldValues>,
      'email' | 'mobile_phone'
    > & { full_name: string } = { full_name: '' };

    let first = '';
    let last = '';

    for (const fv of Object.values(record.fields)) {
      if (isEmailValue(fv)) {
        result.email = fv;
      } else if (isFirstNameValue(fv)) {
        first = fv.value;
      } else if (isLastNameValue(fv)) {
        last = fv.value;
      } else if (isMobilePhoneValue(fv)) {
        result.mobile_phone = fv;
      }
    }

    result.full_name = `${first} ${last}`.trim();

    return result;
  }, [record]);

  const phonenumber = mobile_phone?.value?.e164;

  return (
    <StyledListCardItem>
      <ListCardRow>
        <ContactItemCell>
          <TextEllipsis type="link" to={`/client/${record.id}/details`}>
            {full_name || NO_VALUE}
          </TextEllipsis>
        </ContactItemCell>
      </ListCardRow>
      <ListCardRow>
        <ContactItemCell>
          {!email && <TextEllipsis as="span">{NO_VALUE}</TextEllipsis>}
          {email && <TextEllipsis as="span">{email.value}</TextEllipsis>}
          {email && (
            <IconButton
              as="a"
              title={t('Send Email')}
              href={`mailto:${email.value}`}
              sizing="dense"
              color={colorsButton.iconGray}
            >
              <EmailIcon icon="email" />
            </IconButton>
          )}
        </ContactItemCell>
        <ContactItemCell>
          {!phonenumber && <TextEllipsis as="span">{NO_VALUE}</TextEllipsis>}
          {phonenumber && <TextEllipsis as="span">{phonenumber}</TextEllipsis>}
          {phonenumber && (
            <IconButton
              title={t('Call')}
              as="a"
              href={`tel:${phonenumber}`}
              sizing="dense"
              color={colorsButton.iconGray}
            >
              <PhoneIcon icon="phone" />
            </IconButton>
          )}
        </ContactItemCell>
      </ListCardRow>
    </StyledListCardItem>
  );
};

export const ContactListHeader = ({
  label,
  sort = null,
  onChangeSort,
}: ContactListHeaderProps) => {
  return (
    <StyledListCardHeader>
      <ListCardCell>
        <TextEllipsis as="span" weight="bold">
          {label}
        </TextEllipsis>
        <ListCardSortButton
          sort={sort && sort.column === 'full_name' ? sort : null}
          onChangeSort={(direction: 'asc' | 'desc') => {
            if (onChangeSort) {
              onChangeSort({
                column: 'full_name',
                direction,
              });
            }
          }}
        />
      </ListCardCell>
    </StyledListCardHeader>
  );
};
